import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { useField, useFormikContext } from 'formik';
import { FormSimpleAutoComplete } from '../index';
import { ListParams } from '../../types/main';
import { apiCall } from '../../store/effects';
import UserService from '../../services/UserService';
import { Project } from '../../types/items';
import ProjectService from '../../services/ProjectService';

type Props = {
  userOwnerId?: number;
  disabled?: boolean;
  name?: string;
  label?: string;
  defaultValue?: null | string | number;
};

const ProjectSelectField = ({
  userOwnerId,
  disabled = false,
  name = 'project_id',
  label = 'Project',
  defaultValue = null,
}: Props) => {
  const dispatch = useDispatch();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(false);
  const { setFieldValue } = useFormikContext();
  const fieldValue = useField({ name })[0].value;
  const params: ListParams = {
    page_size: 10,
    sort_key: 'updated',
    sort_dir: 'DESC',
  };

  const getProjects = useCallback(async (listParams, initialCall = false) => {
    setLoading(true);
    const projectParams = { ...listParams };
    if (userOwnerId && userOwnerId > 0) {
      const filters = projectParams.filter || {};
      filters.user_owner_id = userOwnerId.toString();
      projectParams.filter = filters;
    }
    const fetchProjects = await ProjectService.list(projectParams);
    setProjects(fetchProjects.items);
    if (!initialCall && !fetchProjects.items.find((item) => item.id === fieldValue)) {
      setFieldValue(name as never, defaultValue);
    }
    setLoading(false);
  }, [setProjects, setLoading, userOwnerId, setFieldValue, defaultValue]);

  // Init call
  /* eslint react-hooks/exhaustive-deps: 0 */
  useEffect(() => {
    if (userOwnerId && userOwnerId > 0) {
      apiCall(UserService.list, { ...params });
      getProjects({ ...params }, true);
    }
  }, [getProjects, disabled, userOwnerId]);

  const searchUsers = useCallback((search) => {
    getProjects({ ...params, filter: { search } });
  }, [dispatch, getProjects, params]);

  const projectList = useMemo(() => projects?.map((item) => ({
    label: item.title,
    id: item.id,
  })), [projects]);

  return (
    <FormSimpleAutoComplete
      options={projectList}
      label={label}
      name={name}
      onSearch={searchUsers}
      loading={loading}
      disabled={disabled}
      defaultValue={defaultValue}
    />
  );
};


export default ProjectSelectField;
